// client/features/routeLists/AddMarksToRoutePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../services/api';
import { useSocket } from '../../../../context/SocketContext';
import styles from './AddMarksToRoutePage.module.css';

const AddMarksToRoutePage = () => {
  const [routes, setRoutes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate]   = useState(new Date());
  const [rangesByRoute, setRangesByRoute] = useState({});
  const [savedStatus, setSavedStatus]     = useState({});
  const socket = useSocket();
  const startRef = useRef(startDate);
  const endRef   = useRef(endDate);
  const [marksByRoute, setMarksByRoute] = useState({});
  const [serverResponses, setServerResponses] = useState({}); // новый стейт

  // Keep refs up to date for socket filters
  useEffect(() => {
    startRef.current = startDate;
    endRef.current   = endDate;
  }, [startDate, endDate]);

  // Load catalog once
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/catalog-product/products');
        setCatalog(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error('Ошибка загрузки каталога:', e);
      }
    })();
  }, []);

  // Fetch routes when date filters change
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const res = await api.get('/routes/by-date', {
          params: {
            startDate: startDate.toISOString().slice(0,10),
            endDate:   endDate.toISOString().slice(0,10)
          }
        });
        const list = Array.isArray(res) ? res : [];
        setRoutes(list);

        // Initialize one range per route
        const initial = {};
        list.forEach(r => {
          initial[r.id] = [{
            date: r.route_date,
            fromSerial: '',
            toSerial: '',
            gtin: '',
            name: ''
          }];
        });
        setRangesByRoute(initial);
      } catch (e) {
        console.error('Ошибка загрузки маршрутов:', e);
      }
    };
    fetchRoutes();
  }, [startDate, endDate]);

  // WebSocket subscribe/unsubscribe
  useEffect(() => {
    if (!socket) return;
    socket.emit('subscribe:routes');
    const onNew = route => {
      const d = route.route_date;
      const s = startRef.current.toISOString().slice(0,10);
      const e = endRef.current.toISOString().slice(0,10);
      if (d >= s && d <= e) {
        setRoutes(prev => prev.some(r => r.id===route.id) ? prev : [...prev, route]);
        setRangesByRoute(prev => ({
          ...prev,
          [route.id]: [{ date:route.route_date, fromSerial:'', toSerial:'', gtin:'', name:'' }]
        }));
      }
    };
    const onDel = ({id}) => {
      setRoutes(prev => prev.filter(r=>r.id!==id));
      setRangesByRoute(prev => {
        const nxt = {...prev}; delete nxt[id]; return nxt;
      });
    };
    socket.on('new_route_created', onNew);
    socket.on('route_deleted', onDel);
    return () => {
      socket.off('new_route_created', onNew);
      socket.off('route_deleted', onDel);
      socket.emit('unsubscribe:routes');
    };
  }, [socket]);

  // Range helpers
  const addRange = routeId => {
    setRangesByRoute(prev => ({
      ...prev,
      [routeId]: [
        ...prev[routeId],
        { date:'', fromSerial:'', toSerial:'', gtin:'', name:'' }
      ]
    }));
  };
  const removeRange = (routeId, idx) => {
    setRangesByRoute(prev => ({
      ...prev,
      [routeId]: prev[routeId].filter((_,i)=>i!==idx)
    }));
  };
  const updateRange = (routeId, idx, field, val) => {
    setRangesByRoute(prev => ({
      ...prev,
      [routeId]: prev[routeId].map((r,i)=> i===idx ? {...r, [field]:val} : r)
    }));
  };

  const loadMarks = async (routeId) => {
    try {
      const res = await api.get(`/marks/by-route/${routeId}`);
      setMarksByRoute(prev => ({ ...prev, [routeId]: res }));
    } catch (e) {
      console.error('Ошибка загрузки марок:', e);
    }
  };

  const handleClearMarks = async (routeId) => {
  // Спрашиваем подтверждение
  if (!window.confirm(`Вы уверены, что хотите удалить все марки маршрута №${routeId}?`)) {
    return;
  }

  try {
    // Отправляем запрос на сервер
    await api.delete(`/marks/by-route/${routeId}`);

    // Очищаем локальный стейт для этого маршрута
    setMarksByRoute(prev => ({
      ...prev,
      [routeId]: [],
    }));

    // Выводим сообщение об успехе
    setServerResponses(prev => ({
      ...prev,
      [routeId]: ['Все марки удалены'],
    }));
  } catch (err) {
    console.error('Ошибка удаления всех марок маршрута:', err);
    // Показать пользователю ошибку
    const errMsg = err.response?.data?.error || err.message;
    alert(`Не удалось удалить все марки: ${errMsg}`);
  }
};


  // Функция сохранения диапазонов на сервере
  const saveRanges = async (routeId) => {
    try {
      // POST возвращает сразу тело ответа, а не { data }
      const res = await api.post(`/marks/${routeId}/add-marks`, {
        ranges: rangesByRoute[routeId].map((r) => ({
          date: r.date,
          fromSerial: r.fromSerial,
          toSerial: r.toSerial,
          gtin: r.gtin,
        })),
      });

      // res теперь — это то, что сервер отдаёт в JSON
      // собираем массив сообщений
      let msgs = [];
      if (Array.isArray(res.messages)) {
        msgs = res.messages;
      } else if (typeof res.message === 'string') {
        msgs = [res.message];
      } else {
        msgs = ['Успешно привязано'];
      }

      // сохраняем в состояние
      setServerResponses((prev) => ({
        ...prev,
        [routeId]: msgs,
      }));

      // визуальный флажок
      setSavedStatus((prev) => ({ ...prev, [routeId]: true }));
      setTimeout(
        () => setSavedStatus((prev) => ({ ...prev, [routeId]: false })),
        1000
      );
    } catch (e) {
      // если сервер вернул тело ошибки — e.response.data, иначе e.message
      const errBody = e.response?.data || e.message;
      console.error('Ошибка добавления марок:', errBody);
      alert('Не удалось добавить марки: ' + JSON.stringify(errBody));
    }
  };



  // Group for duplicates highlighting
  const groups = routes.reduce((acc,r)=>{
    acc[r.route_number]=acc[r.route_number]||[]; acc[r.route_number].push(r);
    return acc;
  },{});

  const compressSerials = (serials) => {
  if (!serials.length) return [];
  const sorted = Array.from(new Set(serials)).sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    if (curr === end + 1) {
      end = curr;
    } else {
      ranges.push(start === end ? `${start}` : `${start}–${end}`);
      start = end = curr;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}–${end}`);
  return ranges;
};

  return (
    <div className={styles.container}>
      <header className={styles.dashboardHeader}>
        <h1>Добавление марок к маршрутам</h1>
        <div className={styles.dateFilter}>
          <label>
            С:
            <input
              type="date"
              className={styles.input}
              value={startDate.toISOString().slice(0,10)}
              onChange={e=>setStartDate(new Date(e.target.value))}
            />
          </label>
          <label>
            По:
            <input
              type="date"
              className={styles.input}
              value={endDate.toISOString().slice(0,10)}
              onChange={e=>setEndDate(new Date(e.target.value))}
            />
          </label>
        </div>
      </header>

      <main>
        {routes.length===0
          ? <p>Нет маршрутов за выбранный период.</p>
          : <div className={styles.routeCardContainer}>
              {routes.map(route=>{
                const isOrig = groups[route.route_number][0].id===route.id && groups[route.route_number].length>1;
                const isDup  = !isOrig && groups[route.route_number].length>1;
                const ranges = rangesByRoute[route.id]||[];

                return (
                  <div key={route.id}
                       className={[styles.routeCard,
                                  isOrig&&styles.duplicateOriginal,
                                  isDup&&styles.duplicate]
                                 .filter(Boolean).join(' ')}>
                    <div className={styles.routeHeader}>
                      <h3>Маршрут №{route.route_number} ({route.route_date})</h3>
                    </div>
                    <div className={styles.routeMetaGrid}>
                      <p><strong>Районы:</strong> {route.route_districts}</p>
                      <p><strong>Водитель:</strong> {route.driver||'—'}</p>
                      <p><strong>Грузчик1:</strong> {route.loader1||'—'}</p>
                      <p><strong>Грузчик2:</strong> {route.loader2||'—'}</p>
                      <p><strong>Машина:</strong> {route.car}</p>
                      <p><strong>Статус:</strong> {route.route_status}</p>
                    </div>

                    {ranges.map((r,idx)=>(
                      <div key={idx} className={styles.rangeContainer}>
                        <button
                          className={styles.removeBtn}
                          onClick={()=>removeRange(route.id,idx)}
                          disabled={ranges.length===1}
                        >&times;</button>
                        {/* Row 1: Date & Serial range */}
                        <div className={styles.rangeRow}>
                          <input
                            type="date"
                            className={styles.input}
                            value={r.date}
                            onChange={e=>updateRange(route.id,idx,'date',e.target.value)}
                          />
                          <input
                            type="number"
                            placeholder="От"
                            className={styles.input}
                            value={r.fromSerial}
                            onChange={e=>updateRange(route.id,idx,'fromSerial',e.target.value)}
                          />
                          <input
                            type="number"
                            placeholder="До"
                            className={styles.input}
                            value={r.toSerial}
                            onChange={e=>updateRange(route.id,idx,'toSerial',e.target.value)}
                          />
                        </div>
                        {/* Row 2: Product select */}
                        <div className={styles.rangeRow}>
                          <select
                            className={styles.input}
                            value={r.name}
                            onChange={e=>{
                              const name=e.target.value;
                              const match=catalog.find(c=>c.name===name);
                              updateRange(route.id,idx,'name',name);
                              updateRange(route.id,idx,'gtin',match?match.gtin:'');
                            }}>
                            <option value="">-- товар --</option>
                            {catalog.map(item=>(
                              <option key={item.id} value={item.name}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Row 3: GTIN input */}
                        <div className={styles.rangeRow}>
                          <input
                            type="text"
                            placeholder="GTIN"
                            className={styles.input}
                            value={r.gtin}
                            onChange={e=>updateRange(route.id,idx,'gtin',e.target.value)}
                          />
                        </div>
                      </div>
                    ))}



                    <button
                      className={styles.addBtn}
                      onClick={()=>addRange(route.id)}
                    >+ Добавить диапазон</button>

                <div className={styles.routeCardFooter}>
                  <button
                    className={styles.submitBtn}
                    onClick={() => saveRanges(route.id)}
                  >
                    {savedStatus[route.id] ? 'Сохранено!!!' : 'Добавить марки'}
                  </button>

                  {Array.isArray(serverResponses[route.id]) && (
                    <div className={styles.serverMessage}>
                      {serverResponses[route.id].map((msg, i) => (
                        <div key={i}>{msg}</div>
                      ))}
                    </div>
                  )}
                    <button
                      className={styles.viewBtn}
                      onClick={() => loadMarks(route.id)}
                    >
                      Посмотреть марки
                    </button>
                   <button
                     className={styles.clearAllBtn}
                     onClick={() => handleClearMarks(route.id)}
                   >
                     Удалить все марки
                   </button>
                      {marksByRoute[route.id] && marksByRoute[route.id].length > 0 && (
                        <div className={styles.marksList}>
                          {(() => {
                            // группируем по GTIN
                            const byGtin = marksByRoute[route.id].reduce((acc, m) => {
                              const key = m.gtin || '_no_gtin';
                              if (!acc[key]) acc[key] = [];
                              acc[key].push(m.serial_number);
                              return acc;
                            }, {});

                            // для каждой группы формируем строку
                            return Object.entries(byGtin)
                              .map(([gtin, serials]) => {
                                const ranges = compressSerials(serials);
                                const label = gtin === '_no_gtin' ? '' : `GTIN ${gtin}: `;
                                return label + ranges.join(', ');
                              })
                              .join(' | ');
                          })()}
                        </div>
                      )}
                </div>
                  </div>
                );
              })}
            </div>
        }
      </main>
    </div>
  );
};

export default AddMarksToRoutePage;
